// Metrics Enum
import invert from 'lodash/invert';
import metricsMeta, { metricKeys, MetricTypes } from '@pulse/metrics';
import { CURRENT_PRODUCT_MODE } from '@utils/constants';
import { odpPlaceholderMetric } from '../odp/utils';

// Ad Content metrics order
export const AD_CONTENT_METRICS_ORDER = [
  metricKeys.AdAttempts,
  metricKeys.AdBitrate,
  metricKeys.AdConcurrentPlays,
  metricKeys.AdEndedPlays,
  metricKeys.AdEndedPlaysPerUniqueDevice,
  metricKeys.AdExitBeforeVideoStartsPercentage,
  metricKeys.AdPlays,
  metricKeys.AdPlaysPercentage,
  metricKeys.AdRebufferingRatio,
  metricKeys.AdVideoStartFailuresPercentage,
  metricKeys.AdVideoStartTime,
  metricKeys.AdVideoPlaybackFailuresPercentage,
  metricKeys.AdExitBeforeVideoStarts,
  metricKeys.AdVideoStartFailures,
  metricKeys.AdVideoPlaybackFailures,
  metricKeys.AdConnectionInducedRebufferingRatio,
  metricKeys.AdFramerate,
  metricKeys.AdMinutesPlayedPerEndedPlay,
  metricKeys.AdMinutesPlayedPerUniqueDevice,
  metricKeys.AdVideoRestartTime,
  metricKeys.AdUniqueDevices,
  metricKeys.AdAverageLifePlayingTimeMins,
  metricKeys.AdActivePlays,
  metricKeys.AdActualDuration,
  metricKeys.AdCompletedCreativePlays,
  metricKeys.ExitsDuringPreRoll,
  metricKeys.ExitsDuringPreRollPct,
  metricKeys.AdRelatedBufferingRatio,
  metricKeys.RebufferingDuringAdsRatio,
];

// Metric line charts in Metrics Container
const METRICS_DEFAULT_ORDER = {
  APP: [
    metricKeys.AppTotalUserEvents,
    metricKeys.AppTotalEvents,
    metricKeys.AppSubscribers,
    metricKeys.AppUniqueDevices,
    metricKeys.AppPageLoads,
    metricKeys.AppAvgPageLoadTime,
    metricKeys.AverageMinutesPerPage,
    metricKeys.AppScreenLoads,
    metricKeys.AppAvgScreenLoadTime,
    metricKeys.AppCrashSessions,
  ],
  EI: [
    metricKeys.Attempts,
    metricKeys.VideoStartFailures, // becomes VideoStartFailuresPercentage
    metricKeys.VideoStartFailuresBusiness, // becomes VideoStartFailuresBusinessPercentage
    metricKeys.VideoStartFailuresTech, // becomes VideoStartFailuresTechPercentage
    metricKeys.ExitBeforeVideoStarts, // becomes ExitBeforeVideoStartsPercentage
    metricKeys.Plays,
    metricKeys.PlaysPercentage,
    metricKeys.PercentageComplete,
    metricKeys.ConcurrentPlays,
    metricKeys.RebufferingRatio,
    metricKeys.ConnectionInducedRebufferingRatio,
    metricKeys.ZeroCIRREndedPlays,
    metricKeys.NonZeroCIRREndedPlays,
    metricKeys.VideoPlaybackFailures, // becomes VideoPlaybackFailuresPercentage
    metricKeys.VideoPlaybackFailuresBusiness, // becomes VideoPlaybackFailuresBusinessPercentage
    metricKeys.VideoPlaybackFailuresTech, // becomes VideoPlaybackFailuresBusinessPercentage
    metricKeys.VideoStartTime,
    metricKeys.VideoRestartTime,
    metricKeys.Bitrate,
    metricKeys.Framerate,
    metricKeys.EndedPlays,
    metricKeys.UniqueDevices,
    metricKeys.Minutes,
    metricKeys.MinutesPerUniqueDevice,
    metricKeys.MinutesPerEndedPlay,
    metricKeys.IntervalMinutesPlayed,
    metricKeys.PausedTime,
    metricKeys.PausedRatio,
    metricKeys.LifeJoinedAvgBitrate,
    metricKeys.LifeAvgNumBitrateSwitches,
    metricKeys.AvgBitrate,
    metricKeys.StreamingPerformanceIndex,
    metricKeys.SpiStreams,
    metricKeys.HighRebuffering,
    metricKeys.HighStartupTime,
    metricKeys.LowBitrate,
    metricKeys.HighExitBeforeVideoStart,
    metricKeys.BadUniqueDevices,
    metricKeys.BadSession,
    metricKeys.ExitDueToCIR,
    metricKeys.LifeDropFrameRatio,

    ...AD_CONTENT_METRICS_ORDER,
  ],
  AI: [
    metricKeys.Plays,
    metricKeys.AdTotalMinutes,
    metricKeys.AdCompletedCreativePlays,
    metricKeys.ConcurrentPlays,
    metricKeys.Attempts,
    metricKeys.PlaysPercentage,
    metricKeys.VideoStartFailures,
    metricKeys.VideoPlaybackFailures,
    metricKeys.AdsNotStarted,
    metricKeys.ExitBeforeVideoStarts,
    metricKeys.AdSlateDurationPct,
    metricKeys.AdSlatePlaysPct,
    metricKeys.RebufferingRatio,
    metricKeys.Bitrate,
    metricKeys.VideoStartTime,
    metricKeys.AdActualDuration,
    metricKeys.EndedPlaysPerUniqueDevice,
    metricKeys.EndedPlays,
    metricKeys.UniqueDevices,
    metricKeys.MinutesPerUniqueDevice,
  ],
  ODP: [odpPlaceholderMetric.name],
  ECO_SERVER: [odpPlaceholderMetric.name],
};

// Special behavior for the following metrics
// Metrics Panel - Shows Percentage metric version of the metric
// Dimension Tables - Show non-percentage metric
// Diagnostics Chart - Shows Percentage metric version of the metrics
// Redux store : metricsContainer.selectedMetric - Always shows non-percentage metric
// It is upto each container to convert the metrics from the store the metric that is needed
// to show for that container
const METRIC_SWITCH_MAP = {
  [metricKeys.VideoStartFailuresPercentage]: metricKeys.VideoStartFailures,
  [metricKeys.ExitBeforeVideoStartsPercentage]: metricKeys.ExitBeforeVideoStarts,
  [metricKeys.VideoPlaybackFailuresPercentage]: metricKeys.VideoPlaybackFailures,
  [metricKeys.AdsNotStartedPct]: metricKeys.AdsNotStarted,
  [metricKeys.AdsWithErrorsPct]: metricKeys.AdsWithErrors,
  [metricKeys.VideoStartFailuresBusinessPercentage]: metricKeys.VideoStartFailuresBusiness,
  [metricKeys.VideoStartFailuresTechPercentage]: metricKeys.VideoStartFailuresTech,
  [metricKeys.VideoPlaybackFailuresBusinessPercentage]: metricKeys.VideoPlaybackFailuresBusiness,
  [metricKeys.VideoPlaybackFailuresTechPercentage]: metricKeys.VideoPlaybackFailuresTech,

  // Experience dashboard metrics
  [metricKeys.HighExitBeforeVideoStartPercentage]: metricKeys.HighExitBeforeVideoStart,
  [metricKeys.HighRebufferingPercentage]: metricKeys.HighRebuffering,
  [metricKeys.HighStartupTimePercentage]: metricKeys.HighStartupTime,
  [metricKeys.LowBitratePercentage]: metricKeys.LowBitrate,
  [metricKeys.BadUniqueDevicesPct]: metricKeys.BadUniqueDevices,
  [metricKeys.BadSessionPct]: metricKeys.BadSession,
  [metricKeys.ZeroCIRREndedPlaysPct]: metricKeys.ZeroCIRREndedPlays,
  [metricKeys.NonZeroCIRREndedPlaysPct]: metricKeys.NonZeroCIRREndedPlays,
  [metricKeys.ExitDueToCIRPct]: metricKeys.ExitDueToCIR,
  // Ad Content metrics
  [metricKeys.AdVideoStartFailuresPercentage]: metricKeys.AdVideoStartFailures,
  [metricKeys.AdVideoPlaybackFailuresPercentage]: metricKeys.AdVideoPlaybackFailures,
  [metricKeys.AdExitBeforeVideoStartsPercentage]: metricKeys.AdExitBeforeVideoStarts,
  // Pre-Roll metrics
  [metricKeys.ExitsDuringPreRollPct]: metricKeys.ExitsDuringPreRoll,

  //App Metrics
  [metricKeys.AppErrorsPercentage]: metricKeys.AppErrors,
  [metricKeys.FailedNetworkRequestPercentage]: metricKeys.FailedNetworkRequest,
  [metricKeys.AppPageLoadCompletePercentage]: metricKeys.AppPageLoadComplete,
  [metricKeys.AppCrashSessionsPercentage]: metricKeys.AppCrashSessions,

  // App SPI metrics
  [metricKeys.AppSPIHighScreenLoadRate]: metricKeys.AppSPIHighScreenLoadCount,
  [metricKeys.AppSPIHighPageLoadRate]: metricKeys.AppSPIHighPageLoadCount,
  [metricKeys.AppSPIHighAppStartupRate]: metricKeys.AppSPIHighAppStartupCount,
  [metricKeys.AppSPIAppCrashSessionRate]: metricKeys.AppSPIAppCrashSessionCount,
  [metricKeys.AppSPIImpactedSessionRate]: metricKeys.AppSPIImpactedSession,
};
const INVERTED_METRIC_SWITCH_MAP: any = invert(METRIC_SWITCH_MAP);

const TOOLTIP_METRICS = {
  EI: [],
  AI: [],
  APP: [
    metricKeys.AppCrashSessions,
    metricKeys.AppCrashSessionsPercentage,
    metricKeys.AppSPIAppCrashSessionCount,
    metricKeys.AppSPIAppCrashSessionRate,
    metricKeys.AppSPIHighAppStartupCount,
    metricKeys.AppSPIHighAppStartupRate,
    metricKeys.AppSPIHighPageLoadCount,
    metricKeys.AppSPIHighPageLoadRate,
    metricKeys.AppSPIHighScreenLoadCount,
    metricKeys.AppSPIHighScreenLoadRate,
    metricKeys.AppSPIHighAppStartupCount,
    metricKeys.AppSPIHighAppStartupRate,
  ],
  ODP: [],
  ECO_SERVER: [],
};

// calculate percentage on frontend
const LOCAL_METRIC_SWITCH_MAP = {
  [metricKeys.AttemptsPercentageLocal]: metricKeys.Attempts,
  [metricKeys.PlaysPercentageLocal]: metricKeys.Plays,
  [metricKeys.EndedPlaysPercentageLocal]: metricKeys.EndedPlays,
  [metricKeys.AppTotalUserEventsPercentageLocal]: metricKeys.AppTotalUserEvents,
  [metricKeys.AppTotalEventsPercentageLocal]: metricKeys.AppTotalEvents,
  [metricKeys.AppPageLoadsPercentageLocal]: metricKeys.AppPageLoads,
  [metricKeys.AppScreenLoadsPercentageLocal]: metricKeys.AppScreenLoads,
  [metricKeys.UserActiveTimePercentageLocal]: metricKeys.UserActiveTime,
};

const INVERTED_LOCAL_METRIC_SWITCH_MAP = invert(LOCAL_METRIC_SWITCH_MAP);

const ACTIVE_METRICS = {
  EI: Object.entries(metricsMeta.EI)
    .filter(([, attr]: any) => attr.active)
    .reduce((prevObj, [name, attr]) => ({ ...prevObj, [name]: attr }), {}),
  AI: Object.entries(metricsMeta.AI)
    .filter(([, attr]: any) => attr.active)
    .reduce((prevObj, [name, attr]) => ({ ...prevObj, [name]: attr }), {}),
  APP: Object.entries(metricsMeta.APP)
    .filter(([, attr]: any) => attr.active)
    .reduce((prevObj, [name, attr]) => ({ ...prevObj, [name]: attr }), {}),
  ODP: [{ [odpPlaceholderMetric.name]: odpPlaceholderMetric }],
  ECO_SERVER: [{ [odpPlaceholderMetric.name]: odpPlaceholderMetric }],
};

const SORT_BY_METRICS = {
  EI: Object.entries(metricsMeta.EI)
    .filter(([, attr]: any) => attr.sortBy)
    .reduce((prevObj, [name, attr]) => ({ ...prevObj, [name]: attr }), {}),
  AI: Object.entries(metricsMeta.AI)
    .filter(([, attr]: any) => attr.sortBy)
    .reduce((prevObj, [name, attr]) => ({ ...prevObj, [name]: attr }), {}),
  APP: Object.entries(metricsMeta.APP)
    .filter(([, attr]: any) => attr.sortBy)
    .reduce((prevObj, [name, attr]) => ({ ...prevObj, [name]: attr }), {}),
};

export const getAllSortedMetrics = (metricList) => {
  const sortedMetrics = Object.entries(metricList)
    .filter(([, attr]: any) => attr.sortBy)
    .reduce((prevObj, [name, attr]) => ({ ...prevObj, [name]: attr }), {});
  return sortedMetrics;
};

// active metrics based on input metric list (of a product)
export const getAllActiveMetrics = (metricList = {}) => {
  const activeMetrics = Object.entries(metricList)
    .filter(([, attr]: any) => attr.active)
    .reduce((prevObj, [name, attr]) => ({ ...prevObj, [name]: attr }), {});
  return activeMetrics;
};

const ALL_METRICS = metricsMeta;

const AUDIENCE_METRICS = [
  metricKeys.Attempts,
  metricKeys.ConcurrentPlays,
  metricKeys.EndedPlays,
  metricKeys.Plays,
];

export const NOC_APP_METRICS = [
  metricKeys.AppAvgPageLoadTime,
  metricKeys.AppAvgScreenLoadTime,
  metricKeys.AverageRequestDuration,
  metricKeys.AppUniqueDevices,
];

const NOC_METRICS = {
  EI: [
    metricKeys.Attempts,
    metricKeys.Bitrate,
    metricKeys.ConcurrentPlays,
    metricKeys.PlaysPercentage,
    metricKeys.RebufferingRatio,
  ],
  AI: [metricKeys.PlaysPercentage, metricKeys.RebufferingRatio],
  APP: NOC_APP_METRICS,
};

const RT_ON_TRENDS_EXCLUDE_LIST = [
  metricKeys.LifeJoinedAvgBitrate,
  metricKeys.LifeAvgNumBitrateSwitches,
  metricKeys.AvgBitrate,
  metricKeys.ExitDueToCIR,
  metricKeys.LifeDropFrameRatio,
];

const NOC_EXCLUDE_METRICS = [
  // add metric to exclude from noc/real-time
];

/* const METRIC_GROUP = {
  StartupExperience: 'Startup Experience',
}; */

// baseline metrics
const THRESHOLD_BASELINE_MAP = {
  EI: {
    [metricKeys.VideoPlaybackFailuresPercentage]: metricKeys.EndedPlays,
    [metricKeys.VideoPlaybackFailuresTechPercentage]: metricKeys.EndedPlays,
    [metricKeys.VideoStartFailuresPercentage]: metricKeys.Attempts,
    [metricKeys.VideoStartFailuresTechPercentage]: metricKeys.Attempts,
    [metricKeys.ExitBeforeVideoStartsPercentage]: metricKeys.Attempts,
    [metricKeys.VideoStartTime]: metricKeys.Attempts,
    [metricKeys.RebufferingRatio]: metricKeys.ConcurrentPlays,
    [metricKeys.ConnectionInducedRebufferingRatio]: metricKeys.ConcurrentPlays,
    [metricKeys.Bitrate]: metricKeys.ConcurrentPlays,
    [metricKeys.Framerate]: metricKeys.ConcurrentPlays,
  },
  AI: {},
  APP: {
    [metricKeys.AppAvgPageLoadTime]: metricKeys.AppPageLoadComplete, // AppPageLoads
    [metricKeys.AppAvgScreenLoadTime]: metricKeys.AppScreenLoads,
    [metricKeys.AverageRequestDuration]: metricKeys.AverageRequestCount,
    [metricKeys.AppUniqueDevices]: metricKeys.AppUniqueDevices,
  },
  ODP: {},
  ECO_SERVER: {},
};

// by default value > threshold is considered critical
// for inverted metrics value < threshold is considered critical
const THRESHOLD_INVERTED_METRICS = [
  metricKeys.ConcurrentPlays,
  metricKeys.Bitrate,
  metricKeys.Framerate,
];

export const DIMENSION_TYPES_CONSTANTS = {
  mutableGlobalMetadata: 'mutableGlobalMetadata',
  mappedEventName: 'mappedEventName',
  eventTagKeyValueMetadata: 'eventTagKeyValueMetadata',
  immutableGlobalMetadata: 'immutableGlobalMetadata',
  // EI dimension types
  ai: 'ai',
  geo: 'geo',
  content: 'content',
  device: 'device',
};

export const METRICS_TYPES_EXCLUDE_BY_FILTER = {
  [DIMENSION_TYPES_CONSTANTS.mutableGlobalMetadata]: [],
  [DIMENSION_TYPES_CONSTANTS.mappedEventName]: [],
  [DIMENSION_TYPES_CONSTANTS.eventTagKeyValueMetadata]: [
    MetricTypes.appSpecificMetrics,
    MetricTypes.appSpiMetrics,
  ],
  [DIMENSION_TYPES_CONSTANTS.immutableGlobalMetadata]: [],
  [DIMENSION_TYPES_CONSTANTS.ai]: [MetricTypes.eiMetrics],
};

const DEFAULT_CHART_METRICS = {
  EI: [
    metricKeys.Attempts,
    metricKeys.VideoStartFailures,
    metricKeys.VideoPlaybackFailures,
    metricKeys.RebufferingRatio,
    metricKeys.Bitrate,
    metricKeys.ConcurrentPlays,
  ],
  AI: [metricKeys.Plays, metricKeys.RebufferingRatio],
  APP: [
    metricKeys.AppTotalUserEvents,
    metricKeys.AppTotalEvents,
    metricKeys.AppSubscribers,
    metricKeys.AppUniqueDevices,
    metricKeys.AppPageLoads,
    metricKeys.AppAvgPageLoadTime,
    metricKeys.AverageMinutesPerPage,
    metricKeys.AppScreenLoads,
    metricKeys.AppAvgScreenLoadTime,
  ],
}[CURRENT_PRODUCT_MODE];

export const BASE_METRICS = Object.values(THRESHOLD_BASELINE_MAP[CURRENT_PRODUCT_MODE]);

export {
  ACTIVE_METRICS as METRICS,
  ALL_METRICS,
  SORT_BY_METRICS,
  METRICS_DEFAULT_ORDER,
  METRIC_SWITCH_MAP,
  INVERTED_METRIC_SWITCH_MAP,
  AUDIENCE_METRICS,
  NOC_METRICS,
  TOOLTIP_METRICS,
  THRESHOLD_BASELINE_MAP,
  NOC_EXCLUDE_METRICS,
  THRESHOLD_INVERTED_METRICS,
  DEFAULT_CHART_METRICS,
  RT_ON_TRENDS_EXCLUDE_LIST,
  LOCAL_METRIC_SWITCH_MAP,
  INVERTED_LOCAL_METRIC_SWITCH_MAP,
};
